import React, { useEffect, useRef, useState } from "react";
import { saveToken } from "@/lib/session";
import { useNavigate, useSearchParams } from "react-router-dom";
import { CheckCircle2, Loader2, XCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { auth, normalizeError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import NumaMascot from "@/components/NumaMascot";

/**
 * Where the link in the confirmation email lands.
 *
 * It may be opened in a browser that has never signed in - people read mail on
 * the phone - so it takes the session the server hands back rather than
 * assuming one is already there.
 */
export default function VerifyEmail() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { checkAuth } = useAuth();
  const [state, setState] = useState({ status: "working" });
  const ran = useRef(false);

  useEffect(() => {
    // Effects run twice in development; a token is single use.
    if (ran.current) return;
    ran.current = true;

    const token = params.get("token");
    if (!token) {
      setState({ status: "failed", message: "Link di conferma incompleto." });
      return;
    }
    auth
      .verifyEmail(token)
      .then(async (res) => {
        // Arriving from the mailbox is the person's own machine, and the
        // account has just been confirmed: keep them signed in.
        if (res.token) saveToken(res.token, true);
        await checkAuth();
        setState({ status: "done" });
      })
      .catch((err) => {
        const e = normalizeError(err);
        setState({ status: "failed", message: e.message, action: e.action });
      });
  }, [params, checkAuth]);

  return (
    <div className="flex min-h-screen items-center justify-center bg-paper px-6 py-12"
         data-testid="verify-email-page">
      <div className="w-full max-w-[420px] text-center">
        {state.status === "working" && (
          <>
            <Loader2 className="mx-auto h-7 w-7 animate-spin text-assistant" />
            <p className="mt-4 text-[14px] text-ink-muted">Conferma in corso…</p>
          </>
        )}

        {state.status === "done" && (
          <>
            <NumaMascot size={64} className="numa-mark mx-auto" variant={1} />
            <CheckCircle2 className="mx-auto mt-3 h-6 w-6 text-ok" />
            <h1 className="font-serif-display mt-3 text-2xl font-semibold text-ink">
              Indirizzo confermato
            </h1>
            <p className="mt-2 text-[14px] leading-relaxed text-ink-muted">
              Tutto pronto. NUMA è a tua disposizione.
            </p>
            <Button className="mt-5" onClick={() => navigate("/", { replace: true })}
                    data-testid="verify-continue">
              Entra in NUMA
            </Button>
          </>
        )}

        {state.status === "failed" && (
          <>
            <XCircle className="mx-auto h-7 w-7 text-danger" />
            <h1 className="font-serif-display mt-3 text-xl font-semibold text-ink">
              {state.message || "Conferma non riuscita"}
            </h1>
            {state.action && (
              <p className="mt-2 text-[13.5px] leading-relaxed text-ink-muted">{state.action}</p>
            )}
            <Button variant="outline" className="mt-5" onClick={() => navigate("/", { replace: true })}>
              Torna a NUMA
            </Button>
          </>
        )}
      </div>
    </div>
  );
}
